/**
 * A barra de filtros rápidos da tela de Menções (2026-10-30) — o que
 * escolher e por quê está em `quick-filters.ts`.
 *
 * Cada filtro da barra escreve no recorte VIVO, como a Polaridade sempre
 * fez: faceta e escolha curta valem no clique. **O que se DIGITA fica
 * aberto na própria barra** (valoração, CENA, período — segundo ajuste da
 * dona no mesmo dia: "os de texto livre já devem trazer o campo"), e vale
 * quando a pessoa para de digitar (700 ms), sai do campo ou dá Enter —
 * aplicar a cada tecla seria uma consulta por dígito, o mesmo motivo do
 * rascunho do painel. A lista colada fica num quadro que abre: é texto de
 * várias linhas, e um campo de uma linha engoliria as quebras.
 *
 * Os filtros de SELEÇÃO abrem com a caixa de busca sempre à vista e já
 * com o cursor nela (`alwaysSearch`), flutuam sobre a página e fecham ao
 * clicar fora.
 *
 * **Arrastar é pela própria pílula** (sem alça): segurar e mover mais de
 * alguns pixels começa o arraste — um clique curto continua abrindo o
 * filtro. Uma cópia da pílula segue o ponteiro, o lugar dela vira um
 * espaço tracejado, e a barra se reordena AO VIVO enquanto a pessoa
 * move, com os vizinhos deslizando (FLIP). Soltar grava a ordem; Esc
 * cancela. Eventos de ponteiro, não o drag-and-drop do HTML, que não
 * funciona no toque. Começar dentro de um campo de texto não arrasta (é
 * selecionar texto). No teclado: Alt + setas.
 */

import {
  Calendar,
  Check,
  ChevronDown,
  CircleDollarSign,
  FileSpreadsheet,
  Gauge,
  Link2,
  Search,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import type { UploadSummary } from "@/shared/api/api-client";
import { MultiSelectFilter } from "@/shared/components/MultiSelectFilter";
import { Select } from "@/shared/components/Select";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import {
  MENTION_SEARCH_FIELDS,
  MENTION_SEARCH_FIELD_LABELS,
  type MentionSearchField,
} from "@/shared/vocabulary/mention-search-fields";
import { uploadLabel } from "@/features/uploads/upload-shared";
import {
  DIMENSIONS,
  DIMENSION_LABELS,
  type Dimension,
  type MentionFilters,
} from "@/features/mentions/mention-filter-criteria";
import type { useMentionFilters } from "@/features/mentions/use-mention-filters";
import {
  moveQuickFilter,
  quickFilterLabel,
  type QuickFilterKey,
} from "@/features/mentions/quick-filters";

/** As facetas cujas opções vêm do catálogo carregado sob demanda. */
const LOCAL_OPTION_DIMENSIONS = new Set<Dimension>(["polarities", "approaches", "sources", "brands"]);

/** Quanto o ponteiro anda antes de virar arraste — abaixo disso é clique. */
const DRAG_THRESHOLD = 6;
/** Quanto o campo digitado espera a pessoa parar antes de valer. */
const TYPING_DELAY_MS = 700;

const TRISTATE_OPTIONS = {
  titleCitation: [
    { value: "all", label: "Todas" },
    { value: "yes", label: "Com citação" },
    { value: "no", label: "Sem citação" },
  ],
  themeHighlight: [
    { value: "all", label: "Todas" },
    { value: "yes", label: "Com destaque" },
    { value: "no", label: "Sem destaque" },
  ],
  sharedPost: [
    { value: "all", label: "Todos" },
    { value: "yes", label: "Em mais de um" },
    { value: "no", label: "Só neste" },
  ],
  reviewed: [
    { value: "all", label: "Todas" },
    { value: "yes", label: "Só revisadas" },
    { value: "no", label: "Só não revisadas" },
  ],
  edited: [
    { value: "all", label: "Todas" },
    { value: "yes", label: "Só editadas" },
    { value: "no", label: "Só não editadas" },
  ],
} as const;

type Tristate = "all" | "yes" | "no";

function isDimension(key: QuickFilterKey): key is Dimension {
  return (DIMENSIONS as readonly string[]).includes(key);
}

/** Onde apertar o ponteiro é usar o controle, não arrastar: campo de
 *  texto (selecionar) e o miolo de uma lista aberta. */
const NOT_A_DRAG_START =
  'input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"], .multi-filter-body, .toolbar-select-list';

/** Fecha a `<details>` ao clicar fora ou com Esc — o mesmo trato do
 *  `MultiSelectFilter` com `closeOnOutsideClick`. */
function useCloseOutside(ref: RefObject<HTMLDetailsElement | null>) {
  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      const details = ref.current;
      if (details?.open && event.target instanceof Node && !details.contains(event.target)) details.open = false;
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      const details = ref.current;
      if (event.key !== "Escape" || !details?.open || !details.contains(document.activeElement)) return;
      details.open = false;
      details.querySelector("summary")?.focus();
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [ref]);
}

/** Um controle da barra que abre um corpo flutuante, com a cara do
 *  `MultiSelectFilter`. O corpo só monta ABERTO. */
function QuickPopover({
  label,
  summary,
  icon: Icon,
  className = "",
  children,
}: {
  label: string;
  summary: string;
  icon: LucideIcon;
  className?: string;
  children: () => ReactNode;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = useState(false);
  useCloseOutside(ref);
  return (
    <div className={`filter-panel-facet quick-filter ${className}`.trim()}>
      <details className="multi-filter" ref={ref} onToggle={event => setOpen(event.currentTarget.open)}>
        <summary>
          <Icon size={15} aria-hidden="true" />
          <span>{label}</span>
          <small>{summary}</small>
          <ChevronDown size={16} aria-hidden="true" />
        </summary>
        <div className="multi-filter-body">{open && children()}</div>
      </details>
    </div>
  );
}

/**
 * O rascunho de um campo digitado na barra: segue o valor vivo enquanto a
 * pessoa não está nele, e devolve `commit`, que vale na hora (Enter, sair
 * do campo) — e vale sozinho `TYPING_DELAY_MS` depois da última tecla.
 */
function useTypedValue<T>(live: T, same: (a: T, b: T) => boolean, onCommit: (value: T) => void) {
  const [text, setText] = useState(live);
  const editing = useRef(false);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const liveRef = useRef(live);
  liveRef.current = live;

  // Quem chama pode passar um objeto novo a cada render (o período): só
  // troca quando o VALOR mudou, senão seria um render atrás do outro.
  useEffect(() => {
    if (!editing.current) setText(current => (same(current, live) ? current : live));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  useEffect(() => {
    if (same(text, liveRef.current)) return;
    const timer = window.setTimeout(() => commitRef.current(text), TYPING_DELAY_MS);
    return () => window.clearTimeout(timer);
    // `same` é estável por quem chama (função de módulo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const commit = (value: T = text) => {
    if (!same(value, liveRef.current)) commitRef.current(value);
  };
  return {
    text,
    setText,
    commit,
    focusProps: {
      onFocus: () => {
        editing.current = true;
      },
      onBlur: () => {
        editing.current = false;
        commit();
      },
    },
  };
}

const sameText = (a: string, b: string) => a.trim() === b.trim();

/** Um valor mínimo digitado (CENA, valoração), aberto na própria barra. */
function InlineMinimumField({
  icon: Icon,
  label,
  ariaLabel,
  prefix,
  placeholder,
  live,
  sanitize,
  display = text => text,
  inputMode,
  hint,
  onCommit,
}: {
  icon: LucideIcon;
  label: string;
  ariaLabel: string;
  prefix?: string;
  placeholder: string;
  live: string;
  sanitize: (typed: string) => string;
  display?: (text: string) => string;
  inputMode: "numeric" | "decimal";
  hint?: string;
  onCommit: (value: string) => void;
}) {
  const { text, setText, commit, focusProps } = useTypedValue(live, sameText, value => onCommit(value.trim()));
  return (
    <div className={`quick-inline-field ${live.trim() ? "has-value" : ""}`} title={hint}>
      <Icon size={15} aria-hidden="true" />
      <span className="quick-inline-label">{label}</span>
      {prefix && <span className="quick-inline-prefix">{prefix}</span>}
      <input
        type="text"
        inputMode={inputMode}
        autoComplete="off"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={display(text)}
        size={Math.max(4, display(text).length + 1)}
        onChange={event => setText(sanitize(event.target.value))}
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          } else if (event.key === "Escape") {
            setText(live);
          }
        }}
        {...focusProps}
      />
      {text && (
        <button
          type="button"
          className="quick-inline-clear"
          aria-label={`Limpar ${label}`}
          onClick={() => {
            setText("");
            commit("");
          }}
        >
          <X size={13} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

type Period = { dateFrom: string; dateTo: string };
const samePeriod = (a: Period, b: Period) => a.dateFrom === b.dateFrom && a.dateTo === b.dateTo;
/** Data inteira e plausível — digitar o ano passa por 0002, 0020, 0202,
 *  e nenhuma delas pode virar consulta. Vazio é "sem limite". */
const usableDate = (value: string) => value === "" || /^(19|20)\d{2}-\d{2}-\d{2}$/.test(value);

/** O período aberto na barra: as duas datas. As horas ficam no "Mais
 *  filtros" (a barra não as apaga; "Limpar" apaga tudo). */
function InlinePeriodField({ live, onCommit }: { live: Period; onCommit: (period: Period | null) => void }) {
  const { text, setText, commit, focusProps } = useTypedValue<Period>(live, samePeriod, period => {
    if (usableDate(period.dateFrom) && usableDate(period.dateTo)) onCommit(period);
  });
  const field = (key: keyof Period, label: string) => (
    <input
      type="date"
      aria-label={label}
      value={text[key]}
      onChange={event => setText(current => ({ ...current, [key]: event.target.value }))}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit();
        }
      }}
      {...focusProps}
    />
  );
  return (
    <div className={`quick-inline-field is-period ${live.dateFrom || live.dateTo ? "has-value" : ""}`}>
      <Calendar size={15} aria-hidden="true" />
      <span className="quick-inline-label">Período</span>
      {field("dateFrom", "Data inicial")}
      <span className="quick-inline-sep" aria-hidden="true">–</span>
      {field("dateTo", "Data final")}
      {(text.dateFrom || text.dateTo) && (
        <button
          type="button"
          className="quick-inline-clear"
          aria-label="Limpar Período"
          onClick={() => {
            setText({ dateFrom: "", dateTo: "" });
            onCommit(null);
          }}
        >
          <X size={13} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

const formatThousands = (digits: string) => (digits ? Number(digits).toLocaleString("pt-BR") : "");

export function MentionQuickFilters({
  hook,
  keys,
  onChangeKeys,
  showsCena,
  searchError,
  uploadOptions,
  uploadFilter,
  onChangeUploadFilter,
}: {
  hook: ReturnType<typeof useMentionFilters>;
  keys: QuickFilterKey[];
  onChangeKeys: (keys: QuickFilterKey[]) => void;
  showsCena: boolean;
  searchError: string | null;
  uploadOptions: UploadSummary[];
  uploadFilter: number[];
  onChangeUploadFilter?: (uploadIds: number[]) => void;
}) {
  const {
    query,
    setQuery,
    searchIn,
    setSearchIn,
    filters,
    updateFilters,
    setPage,
    facet,
    toggleFacet,
    setFacetExclude,
    clearFacet,
    optionsFor,
    channelsHint,
    vehiclesHint,
    searchChannels,
    searchVehicles,
    requestFilterOptions,
    refFilter,
    refDraft,
    setRefDraft,
    refBusy,
    refError,
    applyPastedReferences,
    clearPastedReferences,
  } = hook;

  const available = (key: QuickFilterKey) => {
    if (key === "minCena") return showsCena;
    if (key === "uploads") return uploadOptions.length > 0 && Boolean(onChangeUploadFilter);
    return true;
  };

  // ── Arrastar ──────────────────────────────────────────────────────
  // `preview` é a ordem enquanto a pessoa arrasta (a barra se reordena ao
  // vivo); `null` fora do arraste. O gesto mora num ref porque os
  // ouvintes da janela não podem depender do render.
  const [preview, setPreview] = useState<QuickFilterKey[] | null>(null);
  const [draggingKey, setDraggingKey] = useState<QuickFilterKey | null>(null);
  const slots = useRef(new Map<QuickFilterKey, HTMLDivElement>());
  const gesture = useRef<{
    key: QuickFilterKey;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    container: HTMLElement;
    order: QuickFilterKey[];
    ghost: HTMLElement | null;
  } | null>(null);
  const suppressClick = useRef(false);
  const flipFrom = useRef<Map<QuickFilterKey, DOMRect> | null>(null);
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const commitOrder = useRef(onChangeKeys);
  commitOrder.current = onChangeKeys;

  const shown = (preview ?? keys).filter(available);

  useEffect(() => {
    const finish = (commit: boolean) => {
      const current = gesture.current;
      gesture.current = null;
      if (!current?.ghost) return;
      current.ghost.remove();
      document.body.classList.remove("is-reordering-filters");
      if (commit) {
        commitOrder.current(current.order);
        // O `click` que o navegador dispara depois do `pointerup` abriria
        // o filtro solto; este o engole. Zera no próximo tick se não vier.
        suppressClick.current = true;
        window.setTimeout(() => {
          suppressClick.current = false;
        }, 0);
      }
      setDraggingKey(null);
      setPreview(null);
    };

    const begin = (current: NonNullable<typeof gesture.current>) => {
      const slot = slots.current.get(current.key);
      if (!slot) return;
      slot.querySelectorAll("details[open]").forEach(details => {
        (details as HTMLDetailsElement).open = false;
      });
      if (document.activeElement instanceof HTMLElement && slot.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      // A cópia que segue o ponteiro: um clone do que está na tela, dentro
      // da própria barra (as regras de estilo dela valem pra ele), fora
      // do fluxo e sem receber ponteiro.
      const ghost = slot.cloneNode(true) as HTMLElement;
      ghost.classList.add("quick-filter-ghost");
      ghost.removeAttribute("data-quick-key");
      ghost.setAttribute("aria-hidden", "true");
      ghost.setAttribute("inert", "");
      ghost.style.width = `${slot.getBoundingClientRect().width}px`;
      current.container.appendChild(ghost);
      current.ghost = ghost;
      window.getSelection?.()?.removeAllRanges();
      document.body.classList.add("is-reordering-filters");
      setDraggingKey(current.key);
      setPreview(current.order);
    };

    const onMove = (event: PointerEvent) => {
      const current = gesture.current;
      if (!current) return;
      if (!current.ghost) {
        if (Math.hypot(event.clientX - current.startX, event.clientY - current.startY) < DRAG_THRESHOLD) return;
        begin(current);
        if (!current.ghost) return;
      }
      event.preventDefault();
      const box = current.container.getBoundingClientRect();
      const x = event.clientX - box.left - current.offsetX;
      const y = event.clientY - box.top - current.offsetY;
      current.ghost.style.transform = `translate(${x}px, ${y}px) rotate(-1.5deg)`;

      const slot = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-quick-key]");
      const target = slot?.dataset.quickKey as QuickFilterKey | undefined;
      if (!slot || !target || target === current.key || slot.parentElement !== current.container) return;
      const rect = slot.getBoundingClientRect();
      const side = event.clientX < rect.left + rect.width / 2 ? "before" : "after";
      const next = moveQuickFilter(current.order, current.key, target, side);
      if (next.every((key, index) => key === current.order[index])) return;
      // FLIP: guarda onde cada um estava; o efeito de layout desliza.
      flipFrom.current = new Map(
        [...slots.current].map(([key, element]) => [key, element.getBoundingClientRect()])
      );
      current.order = next;
      setPreview(next);
    };
    const onUp = () => finish(true);
    const onCancel = () => finish(false);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && gesture.current?.ghost) finish(false);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKey);
      gesture.current?.ghost?.remove();
      document.body.classList.remove("is-reordering-filters");
    };
  }, []);

  // Os vizinhos deslizam do lugar antigo pro novo, em vez de pularem.
  useLayoutEffect(() => {
    const before = flipFrom.current;
    flipFrom.current = null;
    if (!before) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    for (const [key, element] of slots.current) {
      const from = before.get(key);
      if (!from || key === draggingKey || typeof element.animate !== "function") continue;
      const to = element.getBoundingClientRect();
      const dx = from.left - to.left;
      const dy = from.top - to.top;
      if (!dx && !dy) continue;
      element.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }], {
        duration: 180,
        easing: "cubic-bezier(.2, .8, .2, 1)",
      });
    }
  }, [preview, draggingKey]);

  const startGesture = (key: QuickFilterKey, event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || gesture.current) return;
    if (event.target instanceof Element && event.target.closest(NOT_A_DRAG_START)) return;
    const slot = event.currentTarget;
    const container = slot.parentElement;
    if (!container) return;
    const rect = slot.getBoundingClientRect();
    gesture.current = {
      key,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      container,
      order: [...keysRef.current],
      ghost: null,
    };
  };

  // Teclado: Alt + setas trocam de lugar com o vizinho VISÍVEL (um filtro
  // guardado mas fora do contexto, como CENA oculto, não conta).
  const moveByKeyboard = (key: QuickFilterKey, event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!event.altKey) return;
    const delta =
      event.key === "ArrowLeft" || event.key === "ArrowUp"
        ? -1
        : event.key === "ArrowRight" || event.key === "ArrowDown"
          ? 1
          : 0;
    if (!delta) return;
    event.preventDefault();
    const neighbor = shown[shown.indexOf(key) + delta];
    if (neighbor) onChangeKeys(moveQuickFilter(keys, key, neighbor, delta < 0 ? "before" : "after"));
  };

  const swallowClickAfterDrag = (event: ReactMouseEvent) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  // ── Catálogo e escrita ────────────────────────────────────────────
  const needsCatalog = shown.some(key => isDimension(key) && !LOCAL_OPTION_DIMENSIONS.has(key));
  useEffect(() => {
    if (needsCatalog) requestFilterOptions();
  }, [needsCatalog, requestFilterOptions]);

  // Toda mudança da barra é um recorte novo: volta pra página 1, como o
  // "Aplicar" do painel.
  const change = (update: (current: MentionFilters) => MentionFilters) => {
    updateFilters(update);
    setPage(1);
  };

  const renderFilter = (key: QuickFilterKey): ReactNode => {
    if (isDimension(key)) {
      return (
        <div className="filter-panel-facet quick-filter">
          <MultiSelectFilter
            label={DIMENSION_LABELS[key]}
            hint={key === "channels" ? channelsHint : key === "vehicles" ? vehiclesHint : undefined}
            search={key === "channels" ? searchChannels : key === "vehicles" ? searchVehicles : undefined}
            options={optionsFor(key)}
            selected={facet(key).values}
            exclude={facet(key).exclude}
            onExcludeChange={value => setFacetExclude(key, value)}
            onToggle={value => toggleFacet(key, value)}
            onClear={() => clearFacet(key)}
            closeOnOutsideClick
            alwaysSearch
            // Clipadora é inclusão pura (o servidor não exclui por ela).
            excludable={key !== "clippingServices"}
          />
        </div>
      );
    }
    switch (key) {
      case "search":
        return (
          <div className="quick-filter-search">
            <div className="search-field">
              <Search size={17} />
              <input
                name="mention-search"
                autoComplete="off"
                aria-label="Buscar menções"
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Buscar por título ou conteúdo…"
                title="Aceita AND, OR, NOT e parênteses. Aspas = palavra ou frase inteira. Sem operador entre palavras vale AND."
              />
              {/* Lista fechada e curta: o `Select` da casa (2026-10-18: o menu nativo
                  não aceita raio nem cor, e a dona quer um dropdown só), não SearchSelect. */}
              <Select
                aria-label="Buscar em"
                value={searchIn}
                onChange={event => setSearchIn(event.target.value as MentionSearchField)}
              >
                {MENTION_SEARCH_FIELDS.map(field => (
                  <option key={field} value={field}>
                    {MENTION_SEARCH_FIELD_LABELS[field]}
                  </option>
                ))}
              </Select>
            </div>
            {searchError && (
              <p className="search-field-hint" role="alert">
                {searchError}
              </p>
            )}
          </div>
        );
      case "period":
        return (
          <InlinePeriodField
            live={{ dateFrom: filters.dateFrom, dateTo: filters.dateTo }}
            onCommit={period =>
              change(current =>
                period
                  ? { ...current, ...period }
                  : { ...current, dateFrom: "", dateTo: "", timeFrom: "", timeTo: "" }
              )
            }
          />
        );
      case "minCena":
        return (
          <InlineMinimumField
            icon={Gauge}
            label="CENA mín."
            ariaLabel="CENA mínimo"
            placeholder="6,0"
            inputMode="decimal"
            live={filters.minCena}
            // Vírgula vira ponto (é o que o servidor lê); só um separador.
            sanitize={typed => typed.replace(",", ".").replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1").slice(0, 4)}
            display={text => text.replace(".", ",")}
            onCommit={value => change(current => ({ ...current, minCena: value }))}
          />
        );
      case "minAdValue":
        return (
          <InlineMinimumField
            icon={CircleDollarSign}
            label="Valoração mín."
            ariaLabel="Valoração mínima em reais"
            prefix="R$"
            placeholder="5.000"
            inputMode="numeric"
            live={filters.minAdValue}
            // Reais inteiros, como no painel (a trava por hash do relatório
            // proíbe fracionário); o ponto de milhar é só de exibição.
            sanitize={typed => typed.replace(/\D/g, "").replace(/^0+/, "").slice(0, 12)}
            display={formatThousands}
            hint="Recorta a imprensa e deixa as menções de redes passarem."
            onCommit={value => change(current => ({ ...current, minAdValue: value }))}
          />
        );
      case "titleCitation":
      case "themeHighlight":
      case "sharedPost":
      case "reviewed":
      case "edited": {
        const label = quickFilterLabel(key);
        return (
          <div className="quick-filter-select">
            <ToolbarSelect<Tristate>
              label={label}
              ariaLabel={label}
              value={filters[key]}
              onChange={value => change(current => ({ ...current, [key]: value }))}
              options={[...TRISTATE_OPTIONS[key]]}
            />
          </div>
        );
      }
      case "onlyDiscarded":
        return (
          <button
            type="button"
            className={`secondary-button ${filters.onlyDiscarded ? "active" : ""}`}
            aria-pressed={filters.onlyDiscarded}
            onClick={() => change(current => ({ ...current, onlyDiscarded: !current.onlyDiscarded }))}
          >
            <Trash2 size={16} />
            Só as descartadas
          </button>
        );
      case "uploads":
        return (
          <QuickPopover
            label="Uploads"
            summary={uploadFilter.length ? `${uploadFilter.length} selecionado${uploadFilter.length === 1 ? "" : "s"}` : "Todos"}
            icon={FileSpreadsheet}
          >
            {() => (
              <>
                <div className="multi-filter-head">
                  <span className="multi-filter-hint">Marcando mais de um, as menções aparecem juntas.</span>
                  <button
                    type="button"
                    className="text-button"
                    disabled={!uploadFilter.length}
                    onClick={() => onChangeUploadFilter?.([])}
                  >
                    Limpar seleção
                  </button>
                </div>
                <div className="multi-filter-options filter-chip-group">
                  {uploadOptions.map(item => {
                    const active = uploadFilter.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`filter-chip ${active ? "active" : ""}`}
                        aria-pressed={active}
                        title={uploadLabel(item)}
                        onClick={() =>
                          onChangeUploadFilter?.(
                            active ? uploadFilter.filter(current => current !== item.id) : [...uploadFilter, item.id]
                          )
                        }
                      >
                        {active && <Check size={13} />}
                        <span className="filter-chip-label">{uploadLabel(item)}</span>
                        <span className="filter-chip-count">{item.mention_count}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </QuickPopover>
        );
      case "pastedRefs":
        return (
          <QuickPopover
            label="Lista colada"
            summary={refFilter ? `${refFilter.postCount} publicaç${refFilter.postCount === 1 ? "ão" : "ões"}` : "Nenhuma"}
            icon={Link2}
            className="quick-filter-wide"
          >
            {() => (
              <div className="quick-filter-form">
                <p className="multi-filter-hint">
                  Um por linha (vírgula e ponto-e-vírgula também separam). Casa pelo link, pelo link original ou
                  pelo código da clipadora.
                </p>
                <textarea
                  rows={5}
                  autoFocus
                  value={refDraft}
                  onChange={event => setRefDraft(event.target.value)}
                  placeholder={"https://…\ncódigo-da-notícia"}
                  aria-label="Links ou códigos colados"
                />
                {refError && (
                  <p className="pasted-refs-error" role="alert">
                    {refError}
                  </p>
                )}
                <div className="quick-filter-form-actions">
                  <button type="button" className="text-button" disabled={!refFilter} onClick={clearPastedReferences}>
                    Limpar
                  </button>
                  <button
                    type="button"
                    className="primary-button"
                    disabled={refBusy || refDraft.trim() === ""}
                    onClick={() => void applyPastedReferences()}
                  >
                    {refBusy ? "Procurando…" : "Aplicar lista"}
                  </button>
                </div>
              </div>
            )}
          </QuickPopover>
        );
    }
  };

  if (!shown.length) {
    return (
      <span className="quick-filter-empty">
        Nenhum filtro na barra — fixe os que quiser pelo alfinete do “Mais filtros”.
      </span>
    );
  }

  return (
    <>
      {shown.map(key => {
        const classes = ["quick-filter-slot"];
        if (key === "search") classes.push("is-search");
        if (draggingKey === key) classes.push("is-placeholder");
        return (
          <div
            key={key}
            ref={element => {
              if (element) slots.current.set(key, element);
              else slots.current.delete(key);
            }}
            className={classes.join(" ")}
            data-quick-key={key}
            aria-roledescription="filtro reordenável"
            aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
            onPointerDown={event => startGesture(key, event)}
            onKeyDown={event => moveByKeyboard(key, event)}
            onClickCapture={swallowClickAfterDrag}
          >
            {renderFilter(key)}
          </div>
        );
      })}
    </>
  );
}
